"use client";

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const products = [
{
  name: '血球分析儀',
  nameEn: 'Haematology Analyser',
  desc: '全自動血球計數分析，提供 CBC 及分類白血球計數，支援高通量臨床檢測需求。',
  icon:
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>

},
{
  name: '生化分析儀',
  nameEn: 'Biochemistry Analyser',
  desc: '全自動生化分析系統，可同時檢測肝腎功能、血脂、血糖等多項生化指標。',
  icon:
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
      </svg>

},
{
  name: '化學發光分析儀',
  nameEn: 'Chemiluminescence Analyser',
  desc: '高靈敏度免疫分析系統，適用於腫瘤標誌物、甲狀腺功能、感染性疾病等多項檢測。',
  icon:
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>

},
{
  name: '配套試劑',
  nameEn: 'Diagnostic Reagents',
  desc: '提供各分析儀專用配套試劑，確保檢測結果的準確性和可靠性，並提供完善的試劑管理服務。',
  icon:
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>

}];


export default function IVDSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="ivd" ref={sectionRef} className="py-24" style={{ background: 'var(--color-background)' }}>
      <div className="max-w-8xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          {/* Image first on desktop */}
          <div className="reveal-on-scroll order-2 lg:order-1 space-y-5">
            <div className="rounded-3xl overflow-hidden shadow-card-hover" style={{ aspectRatio: '16/10' }}>
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_18a31a924-1772903774172.png"
                alt="Laboratory diagnostic equipment including analysers and test tubes"
                width={700}
                height={440}
                className="w-full h-full object-cover" />
              
            </div>
            <div className="p-6 rounded-2xl border border-border bg-white">
              <h4 className="font-display font-700 text-foreground mb-2" style={{ fontWeight: 700, fontSize: '16px' }}>適用科室</h4>
              <div className="flex flex-wrap gap-2">
                {['檢驗科', '血液科', '腫瘤科', '內分泌科', '感染科', '急診科']?.map((tag) =>
                <span key={tag} className="px-3 py-1.5 rounded-lg text-xs font-600 text-accent" style={{ background: '#EEF7E6', fontWeight: 600 }}>{tag}</span>
                )}
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="space-y-8 order-1 lg:order-2">
            <div className="reveal-on-scroll">
              <span className="section-label">體外診斷 · In Vitro Diagnostics</span>
            </div>
            <div className="reveal-on-scroll reveal-delay-1">
              <div className="accent-bar w-12 mb-5" />
              <h2 className="font-display font-800 text-foreground leading-tight" style={{ fontWeight: 800, fontSize: 'clamp(28px, 3.5vw, 44px)' }}>
                體外診斷
                <br />
                <span style={{ color: 'var(--color-accent)' }}>分析儀及試劑</span>
              </h2>
            </div>
            <p className="reveal-on-scroll reveal-delay-2 text-muted leading-relaxed" style={{ fontSize: '16px' }}>
              涵蓋血球、生化、化學發光等多種體外診斷分析儀及相應配套試劑，為臨床實驗室提供精確、高效的診斷解決方案。
            </p>

            {/* Product list */}
            <div className="reveal-on-scroll reveal-delay-3 grid sm:grid-cols-2 gap-4">
              {products?.map((p) =>
              <div key={p?.name} className="flex gap-4 p-4 rounded-xl border border-border bg-white card-hover">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: '#EEF7E6', color: '#6AAF3D' }}>
                    {p?.icon}
                  </div>
                  <div>
                    <div className="font-display font-700 text-foreground text-sm" style={{ fontWeight: 700 }}>{p?.name}</div>
                    <div className="text-muted text-xs">{p?.nameEn}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Product cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {products?.map((p, idx) =>
          <div key={p?.name} className={`reveal-on-scroll reveal-delay-${idx + 1} p-6 rounded-2xl border border-border bg-white card-hover`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: '#EEF7E6', color: '#6AAF3D' }}>
                {p?.icon}
              </div>
              <h3 className="font-display font-700 text-foreground mb-1" style={{ fontWeight: 700, fontSize: '16px' }}>{p?.name}</h3>
              <p className="text-xs text-muted mb-3" style={{ fontWeight: 500 }}>{p?.nameEn}</p>
              <p className="text-muted leading-relaxed" style={{ fontSize: '13px' }}>{p?.desc}</p>
            </div>
          )}
        </div>
      </div>
    </section>);

}